import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate, Link, createSearchParams } from 'react-router-dom';
import { auth, db } from './firebase/firebaseConfig';
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import useAnonymousAuth, {
  GUEST_ACCOUNT_REGISTERED_CODE,
  bootstrapGuestSessionFast,
  prefetchGuestSession,
  reconcileGuestDeviceMap,
  waitForPersistedAnonymousUser,
} from './hooks/useAnonymousAuth';
import { PremiumProvider } from './contexts/PremiumContext';
import { ThemeProvider } from './contexts/ThemeContext';
import SnippetLimitIndicator from './components/SnippetLimitIndicator';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import ReactGA from 'react-ga4';
import { trackDailySession } from './utils/analytics';
import { doc, updateDoc, deleteDoc, collection, getDocs, query, where, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from './firebase/firebaseConfig';
import { storage } from './utils/storage';
import ErrorBoundary from './components/ErrorHandling/ErrorBoundary';
import errorService, { setUserContext, clearUserContext } from './utils/errorHandling';
import Auth from './components/Auth/Auth';
import EmailVerification from './components/Auth/EmailVerification';
import GuestSessionFallback from './components/Auth/GuestSessionFallback';
import AppLoadingShell from './components/AppLoadingShell';
import DeckManager from './components/DeckManagement/DeckManager';
import Study from './components/StudyMode/Study';
import { LearnMode, CourseViewer, CourseGenerator } from './components/LearnMode';
import RoadmapView from './components/Roadmap/RoadmapView';
import {
  ROADMAP_PATHS,
  buildRoleRoadmapLearnPath,
  ROADMAP_GRAPH_LANG_SEARCH_PARAM,
  ROADMAP_GRAPH_NAV_FROM_HOME_STATE,
  ROADMAP_GRAPH_OPEN_TRACK_PREVIEW_STATE_KEY,
  ROADMAP_GRAPH_RESUME_NODE_ID_STATE_KEY,
  ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY,
  ROADMAP_ROLE_ID_SEARCH_PARAM
} from './components/Roadmap/roadmapPaths';
import { writeRoadmapLearnHubMode, ROADMAP_LEARN_MODES } from './components/Roadmap/roadmapLearnHub';
import CommunityPage from './components/Community/CommunityPage';
// import userIcon from './Icons/user.svg'; // Replaced with dynamic profile avatar
import { ReactComponent as DeckIcon } from './Icons/home.svg';
import { ReactComponent as CommunityIcon } from './Icons/community-icon.svg';
import { ReactComponent as LibraryIcon } from './Icons/deck-icon.svg';
import { ReactComponent as SearchIcon } from './Icons/search.svg';
import { ReactComponent as NavBackIcon } from './Icons/nav-back.svg';
import { ReactComponent as NavForwardIcon } from './Icons/nav-forward.svg';
import GlobalSearch from './components/GlobalSearch/GlobalSearch';
import { ChevronDown, Mail, ArrowUpCircle, LogOut, UserPlus, LogIn } from 'lucide-react';
import { FaDiscord } from 'react-icons/fa';
import { ReactComponent as RoadmapNodeIcon } from './Icons/roadmap-node-icon.svg';
import { ReactComponent as ExploreIcon } from './Icons/explore.svg';
// import { ReactComponent as LearnModeIcon } from './Icons/learn-mode.svg'; // Learn side nav disabled for now
import UserCreationMenu from './components/Auxiliary/UserCreationMenu';
import UserCreation from './components/Auxiliary/UserCreation';
import './styles/main.css';
import './styles/App.css';
import Home from './components/Page/Home';
import Explore from './components/Explore/Explore';
import UserProfile from './components/Page/UserProfile';
import Settings from './components/Page/Settings';
import addIcon from './Icons/add.svg';
import SpacedRepetition from './components/SRS/SpacedRepetition';
import { computeUnifiedDueSnippetCount, computeUnifiedDueSnippetCountAfterStudy } from './services/reviewQueueService';
import { useDirectPremiumCheck } from './hooks/useDirectPremiumCheck';
import { SubscriptionPage } from './components/Payments';
import PricingPage from './components/PricingPage/PricingPage';
import Support from './components/Page/Support';
import { ReactComponent as SettingsIcon } from './Icons/settings.svg';
import { ReactComponent as PlaygroundIcon } from './Icons/playground-icon.svg';
import PlaygroundStudyMode from './components/Playground/PlaygroundStudyMode';
import DailyGoalHeader from './components/StudyMode/DailyGoalHeader';
import TokenPurchaseModal from './components/Tokens/TokenPurchaseModal';
import { ensureOnboardingCookbook } from './utils/onboarding';
import GuestSaveModal from './components/Auth/GuestSaveModal';
import GuestSoftGateBanner from './components/Auth/GuestSoftGateBanner';
import PartnerAccessBanner from './components/Payments/PartnerAccessBanner';
import { usePremium } from './contexts/PremiumContext';
import { shouldShowPartnerAccessBanner, navigateToSubscriptionPage } from './utils/partnerAccessExpiry';
import RolePickerModal from './components/Roles/RolePickerModal';
import { useUserProfile, USER_PROFILE_QUERY_KEY, selectPrimaryRoleId } from './hooks/useUserProfile';
import { PRIMARY_ROLE_ID_QUERY_KEY } from './hooks/usePrimaryRoleId';
import { isAccountMergeInProgress } from './utils/accountMerge';
import {
  hasCompletedGuestStudy,
  markGuestStudyCompleted,
  markGuestDemoStarted,
  hasUnsavedGuestProgress,
  isSaveBannerSnoozedForSession,
  snoozeSaveBannerForSession,
  markSaveModalDismissed,
  clearGuestStudyState,
  formatGuestDueLabel,
  getGuestFirstNextReviewIso,
} from './utils/demoState';
import {
  trackDemoStarted,
  trackSessionCompleted,
  trackGateDismissed,
  trackGateShown,
  trackSignupPromptShown,
} from './utils/analytics';
import { normalizeRoadmapLanguageKey } from './utils/roadmapDisplay';
import { userCanAccessRoadmapTrack } from './utils/roadmapSubscription';
import { COOKBOOK_SNIPPET_SKILLS_QUERY_KEY } from './hooks/useCookbookSnippetSkills';

import '@fontsource/inter/400.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/kanit/700.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Initialize the error handling service
const ErrorServiceInitializer = () => {
  useEffect(() => {
    errorService.initErrorService({
      enableConsoleLogging: true,
      enableRemoteLogging: process.env.NODE_ENV === 'production',
      includeStackTraces: process.env.NODE_ENV !== 'production'
    });
  }, []);
  return null;
};

/** Routes + auth: anonymous session on app paths so Firestore rules keep working without a login wall. */
function AppRoutes() {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  // GA4 SPA page_view — initialize() in src/index.js only records the first load,
  // so we send a pageview on every React Router navigation.
  useEffect(() => {
    if (!ReactGA.isInitialized) return;
    ReactGA.send({
      hitType: 'pageview',
      page: `${location.pathname}${location.search}`,
    });
  }, [location.pathname, location.search]);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestSessionError, setGuestSessionError] = useState(null);

  // Guest session: prefetch starts at module load; AppRoutes finishes bootstrap.
  useAnonymousAuth({
    autoStart: false,
  });

  const startGuestSession = useCallback(async (context) => {
    try {
      const guestUser =
        auth.currentUser ||
        (await prefetchGuestSession()) ||
        (await bootstrapGuestSessionFast());

      setGuestSessionError(null);
      setUserContext({
        userId: guestUser.uid,
        isAuthenticated: false,
        sessionId: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
      });
      setUser(guestUser);
      setLoading(false);

      guestUser.getIdToken().catch((tokenErr) => {
        console.warn('Guest auth token prefetch failed:', tokenErr);
      });

      reconcileGuestDeviceMap().catch((err) => {
        console.warn(`Guest device_map reconcile failed (${context}):`, err);
      });
    } catch (err) {
      if (err?.code === GUEST_ACCOUNT_REGISTERED_CODE) {
        setGuestSessionError(null);
        setLoading(false);
        return;
      }
      console.warn(`Anonymous sign-in failed (${context}):`, err);
      setGuestSessionError(err?.code || err?.message || 'anonymous-auth-failed');
      setLoading(false);
    }
  }, []);

  const applyAuthenticatedUser = useCallback((currentUser) => {
    setGuestSessionError(null);
    setUserContext({
      userId: currentUser.uid,
      isAuthenticated: !currentUser.isAnonymous,
      sessionId: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
    });
    setUser(currentUser);
    setLoading(false);

    currentUser.getIdToken().catch((tokenErr) => {
      console.warn('Auth token prefetch failed:', tokenErr);
    });

    if (currentUser.isAnonymous) {
      reconcileGuestDeviceMap().catch((err) => {
        console.warn('Guest session reconcile failed (background):', err);
      });
      return;
    }

    (async () => {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const profileData = {
          uid: currentUser.uid,
          email: currentUser.email ?? '',
          displayName: currentUser.displayName ?? '',
          photoURL: currentUser.photoURL ?? '',
          accountType: 'registered',
        };
        const existingSnap = await getDoc(userRef);
        if (!existingSnap.exists()) {
          profileData.createdAt = serverTimestamp();
        }
        await setDoc(userRef, profileData, { merge: true });
      } catch (profileErr) {
        console.warn('Failed to sync user profile to Firestore:', profileErr);
      }
    })();

    ensureOnboardingCookbook(currentUser).catch((err) => {
      console.warn('Onboarding cookbook failed (non-blocking):', err);
    });
  }, []);

  useLayoutEffect(() => {
    let cancelled = false;

    (async () => {
      const bootstrapPromise = prefetchGuestSession();
      await auth.authStateReady();
      if (cancelled || pathnameRef.current.startsWith('/auth')) return;

      let currentUser = auth.currentUser;
      if (!currentUser && bootstrapPromise) {
        try {
          currentUser = await bootstrapPromise;
        } catch {
          /* onAuthStateChanged / startGuestSession fallback */
        }
      }

      if (cancelled || !currentUser) return;
      applyAuthenticatedUser(currentUser);
    })();

    return () => {
      cancelled = true;
    };
  }, [applyAuthenticatedUser]);

  useEffect(() => {
    let unsubscribe = () => { };

    const initAuth = async () => {
      await auth.authStateReady();
      unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
        const path = pathnameRef.current;

        if (currentUser) {
          applyAuthenticatedUser(currentUser);
        } else {
          if (path.startsWith('/auth')) {
            clearUserContext();
            setUser(null);
            setLoading(false);
            return;
          }

          if (isAccountMergeInProgress()) {
            setLoading(true);
            return;
          }

          if (auth.currentUser?.isAnonymous) {
            applyAuthenticatedUser(auth.currentUser);
            return;
          }

          const bootstrapPromise = prefetchGuestSession();
          if (bootstrapPromise) {
            try {
              const guestUser = await bootstrapPromise;
              applyAuthenticatedUser(guestUser);
              return;
            } catch {
              /* fall through */
            }
          }

          const persistedGuest = await waitForPersistedAnonymousUser(25);
          if (persistedGuest) {
            applyAuthenticatedUser(persistedGuest);
            return;
          }

          clearUserContext();
          setUser(null);
          setLoading(true);
          await startGuestSession('auth-state-change');
        }
      });
    };

    initAuth();

    return () => unsubscribe();
  }, [startGuestSession, applyAuthenticatedUser]);

  useEffect(() => {
    let cancelled = false;

    const ensureAnonymousIfNeededAfterRestore = async () => {
      const path = location.pathname;
      if (path.startsWith('/auth')) return;

      await auth.authStateReady();
      if (cancelled) return;
      if (auth.currentUser) return;

      await startGuestSession('navigation');
    };

    ensureAnonymousIfNeededAfterRestore();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, startGuestSession]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, (currentUser) => {
      if (!currentUser && !pathnameRef.current.startsWith('/auth')) {
        if (!isAccountMergeInProgress() && !auth.currentUser?.isAnonymous) {
          setLoading(true);
        }
      }
      if (!currentUser && isAccountMergeInProgress()) {
        return;
      }
      setUser((prev) => {
        if (!currentUser && !prev) return prev;
        if (currentUser?.uid === prev?.uid) return prev;
        return currentUser;
      });
    });
    return () => unsubscribe();
  }, []);

  const retryGuestSession = () => {
    setGuestSessionError(null);
    setLoading(true);
    startGuestSession('retry');
  };

  const loadingShell = <AppLoadingShell />;

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="/auth" element={<Auth />} />
      <Route
        path="/verify-email"
        element={
          loading ? (
            loadingShell
          ) : user && !user.isAnonymous ? (
            <EmailVerification />
          ) : (
            <Navigate to="/home" replace />
          )
        }
      />
      <Route
        path="/home/*"
        element={
          loading ? (
            loadingShell
          ) : guestSessionError && !user ? (
            <GuestSessionFallback errorMessage={guestSessionError} onRetry={retryGuestSession} />
          ) : user ? (
            <MainAppContent key={user.uid} user={user} />
          ) : (
            <Navigate to="/auth" replace />
          )
        }
      />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/support" element={<Support />} />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

// Main App Component
function App() {
  const renderErrorFallback = () => (
    <div className="error-container">
      <h2>Something went wrong</h2>
      <p>An unexpected error occurred. Please try refreshing the page.</p>
      <button
        type="button"
        onClick={() => {
          window.location.reload();
        }}
        className="retry-button"
      >
        Retry
      </button>
    </div>
  );

  return (
    <ErrorBoundary fallback={renderErrorFallback}>
      <ErrorServiceInitializer />
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <PremiumProvider>
            <Router>
              <AppRoutes />
            </Router>
          </PremiumProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// Separate component for the main app shell (registered or anonymous Firebase user).
function MainAppContent({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAnonymousGuest = Boolean(user?.isAnonymous);
  const { data: userProfileData, isLoading: userProfileLoading } = useUserProfile(
    isAnonymousGuest ? null : user?.uid
  );
  const primaryRoleId = selectPrimaryRoleId(userProfileData?.userData);
  const [rolePickerDismissed, setRolePickerDismissed] = useState(false);
  const showRolePicker =
    !isAnonymousGuest &&
    Boolean(user?.uid) &&
    !userProfileLoading &&
    !primaryRoleId &&
    !rolePickerDismissed;
  const [softGateVisible, setSoftGateVisible] = useState(
    () =>
      Boolean(user?.isAnonymous) &&
      hasUnsavedGuestProgress() &&
      !isSaveBannerSnoozedForSession()
  );
  /** GuestSaveModal visibility — opens when the guest clicks Save on the
   *  soft-gate banner (or Sign up / Create). Not shown automatically after a Check. */
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [guestDueLabel, setGuestDueLabel] = useState(null);
  const openSaveModal = useCallback(() => {
    setSaveModalOpen(true);
  }, []);
  const navigateGuestSignup = useCallback(() => {
    setSaveModalOpen(true);
  }, []);
  const dismissSoftGate = useCallback(() => {
    trackGateDismissed('soft-gate-banner');
    snoozeSaveBannerForSession();
    setSoftGateVisible(false);
  }, []);
  const handleSaveModalDismiss = useCallback(() => {
    markSaveModalDismissed();
    setSaveModalOpen(false);
    setSoftGateVisible(true);
  }, []);
  const handleSaveModalSaved = useCallback(() => {
    clearGuestStudyState();
    setSaveModalOpen(false);
    setSoftGateVisible(false);
    setRolePickerDismissed(false);
  }, []);

  const handleRolePickerComplete = useCallback(
    (roleId) => {
      if (user?.uid) {
        queryClient.invalidateQueries({ queryKey: [USER_PROFILE_QUERY_KEY, user.uid] });
        queryClient.invalidateQueries({ queryKey: [PRIMARY_ROLE_ID_QUERY_KEY, user.uid] });
      }
      setRolePickerDismissed(true);
      if (roleId) {
        writeRoadmapLearnHubMode(ROADMAP_LEARN_MODES.role);
        navigate(buildRoleRoadmapLearnPath(roleId));
        setView('roadmap-menu');
      }
    },
    [queryClient, user?.uid, navigate]
  );

  const [selectedDeck, setSelectedDeck] = useState(null);
  const [studyItem, setStudyItem] = useState(null);
  const [view, setView] = useState('home'); // 'home', 'library', 'study', 'community', 'shared', 'explore', 'profile', ...
  const [previousView, setPreviousView] = useState('home'); // Track the previous view for back navigation
  const [navigationHistory, setNavigationHistory] = useState(['home']); // Track navigation history
  const [historyIndex, setHistoryIndex] = useState(0); // Current position in navigation history
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreationMenu, setShowCreationMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('cookbooks');
  const [isPanelVisible, setIsPanelVisible] = useState(true);
  const [panel_view, setPanelView] = useState('');
  const [panel_activeTab, setPanelActiveTab] = useState('');
  const [dueItemsCount, setDueItemsCount] = useState(0);
  const { isPremiumUser } = useDirectPremiumCheck(user?.uid);
  const { hasPaidSubscription, partnerProUntil } = usePremium();
  const [partnerBannerDismissed, setPartnerBannerDismissed] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  /** Bumped when the user clicks the Library item in the sidebar — closes DeckEditor inside Library (DeckManager). */
  const [libraryNavNonce, setLibraryNavNonce] = useState(0);
  /** True when Library view is showing DeckManager's cookbook editor (Manage Snippets). Drives header back button. */
  const [libraryDeckEditorOpen, setLibraryDeckEditorOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCourseGenerator, setShowCourseGenerator] = useState(false);
  const [deleteCourseFunction, setDeleteCourseFunction] = useState(null);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [lastCompletedNode, setLastCompletedNode] = useState(null); // Optimistic: node that just passed test (for roadmap green)
  /** When study opens from Learn roadmap, preserve pathname/search/state so header Back restores that graph (not generic /graph). */
  const roadmapStudyReturnLocationRef = useRef(null);
  /** When study opens from Profile → Skills, restore that achievements sub-tab after Back (header or in-study). */
  const profileStudyReturnContextRef = useRef(null);
  const prevViewRef = useRef(view);
  const [profileAchievementsPanelRestore, setProfileAchievementsPanelRestore] = useState(null);
  const clearProfileAchievementsPanelRestore = useCallback(() => {
    setProfileAchievementsPanelRestore(null);
  }, []);

  useEffect(() => {
    if (!softGateVisible || !isAnonymousGuest) return;
    trackGateShown({ surface: 'soft-gate-banner' });
    trackSignupPromptShown({ surface: 'soft-gate-banner' });
  }, [softGateVisible, isAnonymousGuest]);

  useEffect(() => {
    if (!isAnonymousGuest || guestDueLabel) return;
    const iso = getGuestFirstNextReviewIso();
    if (iso) setGuestDueLabel(formatGuestDueLabel(iso));
  }, [isAnonymousGuest, guestDueLabel]);

  const userUid = user?.uid ?? null;
  const isPremiumUserRef = useRef(isPremiumUser);
  isPremiumUserRef.current = isPremiumUser;

  const refreshDueItemsCount = useCallback(async ({ afterStudy = false } = {}) => {
    if (!userUid) return;
    const lockedUid = userUid;
    try {
      if (auth.currentUser?.uid !== lockedUid) return;
      const srs = new SpacedRepetition(lockedUid);
      const totalCardsDue = afterStudy
        ? await computeUnifiedDueSnippetCountAfterStudy(
          lockedUid,
          srs,
          isPremiumUserRef.current
        )
        : await computeUnifiedDueSnippetCount(
          lockedUid,
          srs,
          isPremiumUserRef.current
        );
      if (auth.currentUser?.uid !== lockedUid) return;
      setDueItemsCount(totalCardsDue);
    } catch (error) {
      const stale =
        auth.currentUser?.uid !== lockedUid &&
        (error?.code === 'permission-denied' || error?.message?.includes('permissions'));
      if (stale) return;
      errorService.handleError(error, {
        context: { operation: 'fetchDueCounts' }
      });
      setHasError(true);
      setErrorMessage('Unable to load mastery statistics. Please try again later.');
    }
  }, [userUid]);

  /** Study fires progress writes per card — debounce badge refresh to avoid full-library rescans. */
  const dueCountRefreshTimerRef = useRef(null);
  const flushDebouncedDueCountRefresh = useCallback(() => {
    if (dueCountRefreshTimerRef.current) {
      clearTimeout(dueCountRefreshTimerRef.current);
      dueCountRefreshTimerRef.current = null;
    }
  }, []);

  const refreshDueItemsCountDebounced = useCallback(() => {
    flushDebouncedDueCountRefresh();
    dueCountRefreshTimerRef.current = setTimeout(() => {
      dueCountRefreshTimerRef.current = null;
      refreshDueItemsCount({ afterStudy: true });
    }, 30_000);
  }, [refreshDueItemsCount, flushDebouncedDueCountRefresh]);

  useEffect(() => () => flushDebouncedDueCountRefresh(), [flushDebouncedDueCountRefresh]);

  /** Due badge: load on sign-in and when premium status resolves (not on auth token refresh). */
  const prevPremiumForDueCountRef = useRef(undefined);
  useEffect(() => {
    if (!userUid) {
      prevPremiumForDueCountRef.current = undefined;
      return;
    }
    const premiumChanged = prevPremiumForDueCountRef.current !== isPremiumUser;
    const firstRun = prevPremiumForDueCountRef.current === undefined;
    prevPremiumForDueCountRef.current = isPremiumUser;
    if (firstRun || premiumChanged) {
      refreshDueItemsCount();
    }
  }, [userUid, isPremiumUser, refreshDueItemsCount]);

  const handleExitStudy = useCallback((targetView) => {
    flushDebouncedDueCountRefresh();
    setSelectedDeck(null);
    setStudyItem(null);
    queryClient.invalidateQueries({ queryKey: [COOKBOOK_SNIPPET_SKILLS_QUERY_KEY] });
    setView(targetView || 'home');
    refreshDueItemsCount({ afterStudy: true });
  }, [refreshDueItemsCount, flushDebouncedDueCountRefresh, queryClient]);

  const studyExitHandlerRef = useRef(null);
  const leaveStudyViewRef = useRef(null);

  const exitStudyView = useCallback(() => {
    flushDebouncedDueCountRefresh();
    setSelectedDeck(null);
    setStudyItem(null);
    queryClient.invalidateQueries({ queryKey: [COOKBOOK_SNIPPET_SKILLS_QUERY_KEY] });

    const snapshot = roadmapStudyReturnLocationRef.current;
    const startedFromRoadmap =
      previousView === 'roadmap' || previousView === 'roadmap-menu';

    if (startedFromRoadmap && snapshot?.pathname) {
      let prevSurface;
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        prevSurface = navigationHistory[newIndex];
        setHistoryIndex(newIndex);
      } else {
        prevSurface = previousView === 'roadmap-menu' ? 'roadmap-menu' : 'roadmap';
      }
      const pathStr = `${snapshot.pathname}${snapshot.search || ''}`;
      if (
        snapshot.state != null &&
        typeof snapshot.state === 'object' &&
        !Array.isArray(snapshot.state)
      ) {
        navigate(pathStr, { state: snapshot.state });
      } else {
        navigate(pathStr);
      }
      setView(prevSurface);
      roadmapStudyReturnLocationRef.current = null;
      refreshDueItemsCount();
      return;
    }

    roadmapStudyReturnLocationRef.current = null;
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const prevView = navigationHistory[newIndex];
      setHistoryIndex(newIndex);
      setView(prevView);
      if (prevView === 'roadmap-menu') {
        navigate(ROADMAP_PATHS.menu);
      } else if (prevView === 'roadmap') {
        navigate(ROADMAP_PATHS.graph);
      } else {
        navigate('/home');
      }
    } else {
      const fallbackView = previousView && previousView !== 'study' ? previousView : 'home';
      setView(fallbackView);
      if (fallbackView === 'roadmap-menu') {
        navigate(ROADMAP_PATHS.menu);
      } else if (fallbackView === 'roadmap') {
        navigate(ROADMAP_PATHS.graph);
      } else {
        navigate('/home');
      }
    }
    refreshDueItemsCount({ afterStudy: true });
  }, [
    previousView,
    historyIndex,
    navigationHistory,
    navigate,
    refreshDueItemsCount,
    flushDebouncedDueCountRefresh,
    queryClient
  ]);

  const attemptStudyExit = useCallback(
    (customExit) => {
      leaveStudyViewRef.current = typeof customExit === 'function' ? customExit : null;
      const deferred = studyExitHandlerRef.current?.requestExit?.();
      if (deferred) return true;
      if (typeof customExit === 'function') {
        customExit();
        leaveStudyViewRef.current = null;
      } else {
        exitStudyView();
      }
      return false;
    },
    [exitStudyView]
  );

  const setStudySurfaceView = useCallback(
    (nextView) => {
      if (view === 'study' && nextView !== 'study') {
        attemptStudyExit(() => {
          setSelectedDeck(null);
          setStudyItem(null);
          queryClient.invalidateQueries({ queryKey: [COOKBOOK_SNIPPET_SKILLS_QUERY_KEY] });
          setView(nextView);
          refreshDueItemsCount();
        });
        return;
      }
      setView(nextView);
    },
    [view, attemptStudyExit, refreshDueItemsCount, queryClient]
  );

  const handleRegisterStudyExitHandler = useCallback((handler) => {
    studyExitHandlerRef.current = handler;
  }, []);

  const handleNavigateRolePathFromStudy = useCallback(
    (resume) => {
      leaveStudyViewRef.current = null;
      if (!primaryRoleId) {
        exitStudyView();
        return;
      }

      writeRoadmapLearnHubMode(ROADMAP_LEARN_MODES.role);
      if (resume?.resumeTrackId) {
        const trackRef = {
          trackId: resume.resumeTrackId,
          language: resume.resumeLanguage,
          title: resume.resumeTrackTitle
        };
        if (!userCanAccessRoadmapTrack(isPremiumUser, resume.resumeTrackId, trackRef)) {
          window.dispatchEvent(new CustomEvent('navigateToSubscription'));
          exitStudyView();
          return;
        }
        const langKey = normalizeRoadmapLanguageKey(resume.resumeLanguage || 'javascript');
        const params = createSearchParams({
          [ROADMAP_GRAPH_LANG_SEARCH_PARAM]: langKey,
          [ROADMAP_ROLE_ID_SEARCH_PARAM]: primaryRoleId
        });
        exitStudyView();
        navigate(
          {
            pathname: ROADMAP_PATHS.graph,
            search: params.toString()
          },
          {
            state: {
              ...ROADMAP_GRAPH_NAV_FROM_HOME_STATE,
              [ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY]: resume.resumeTrackId,
              [ROADMAP_GRAPH_OPEN_TRACK_PREVIEW_STATE_KEY]: true,
              ...(resume.resumeNodeId
                ? { [ROADMAP_GRAPH_RESUME_NODE_ID_STATE_KEY]: resume.resumeNodeId }
                : {})
            }
          }
        );
        setView('roadmap-menu');
        return;
      }

      exitStudyView();
      navigate(buildRoleRoadmapLearnPath(primaryRoleId));
      setView('roadmap-menu');
    },
    [primaryRoleId, isPremiumUser, exitStudyView, navigate]
  );

  useEffect(() => {
    if (!isAnonymousGuest) return;
    const gatedViews = ['creation', 'course-generator', 'learn', 'course-viewer', 'subscription'];
    if (!gatedViews.includes(view)) return;
    setSaveModalOpen(true);
    setView('home');
  }, [isAnonymousGuest, view]);

  // GA4 daily_session — fire once per local calendar day per non-guest user.
  // localStorage guard prevents inflation from SPA re-mounts (refresh, sign-in
  // hand-off, etc.) within the same day. Guests are excluded because the
  // metric is meant to model DAU/WAU of real accounts.
  useEffect(() => {
    if (!user || isAnonymousGuest) return;
    try {
      const todayKey = new Date().toDateString();
      const storageKey = `flashcode_daily_session_${user.uid}`;
      if (localStorage.getItem(storageKey) === todayKey) return;
      localStorage.setItem(storageKey, todayKey);
      trackDailySession();
    } catch (err) {
      console.warn('daily_session tracking failed:', err);
    }
  }, [user, isAnonymousGuest]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-controls')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Listen for token UI open event
  useEffect(() => {
    const handleOpenTokenUI = () => {
      if (auth.currentUser?.isAnonymous) {
        setSaveModalOpen(true);
        return;
      }
      setShowTokenModal(true);
    };
    window.addEventListener('openTokenUI', handleOpenTokenUI);
    return () => window.removeEventListener('openTokenUI', handleOpenTokenUI);
  }, []);

  // Fetch token balance from backend
  useEffect(() => {
    const fetchTokenBalance = async () => {
      if (!user || user.isAnonymous) return;
      try {
        const functions = getFunctions(app);
        const getTokenBalance = httpsCallable(functions, 'getTokenBalance');
        const result = await getTokenBalance();
        if (result.data && typeof result.data.tokens === 'number') {
          setTokenBalance(result.data.tokens);
        }
      } catch (error) {
        console.error('Error fetching token balance:', error);
        // Set to 0 on error, user can still see modal
        setTokenBalance(0);
      }
    };

    // Load token balance when user changes
    fetchTokenBalance();

    // Handle Stripe redirect (check for purchase=success or purchase=cancelled in URL)
    const urlParams = new URLSearchParams(window.location.search);
    const purchaseStatus = urlParams.get('purchase');
    const subscriptionStatus = urlParams.get('subscription');

    if (purchaseStatus === 'success') {
      // Refresh token balance after successful purchase
      fetchTokenBalance();
      // Show success message (optional)
      // You could add a toast notification here
      console.log('Purchase successful! Token balance will update shortly.');
    } else if (purchaseStatus === 'cancelled') {
      // Handle cancelled purchase (optional)
      // You could show a message here if needed
      console.log('Purchase cancelled');
    }

    // Pro subscription Checkout returns ?subscription=success|canceled|canceled typo — strip from URL bar
    if (subscriptionStatus === 'success') {
      console.log('Subscription checkout completed.');
    }

    // Clean up URL params if present
    if (purchaseStatus || subscriptionStatus) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [user]);


  // Sync Learn roadmap sub-routes (/home/roadmap, /home/roadmap/graph) with view state for nav + browser history
  useEffect(() => {
    const path = (location.pathname || '/').replace(/\/$/, '') || '/';
    if (path === ROADMAP_PATHS.graph) {
      setView('roadmap');
    } else if (path === ROADMAP_PATHS.menu) {
      setView('roadmap-menu');
    } else if (path === '/home') {
      setView((v) => (v === 'roadmap' || v === 'roadmap-menu' ? 'home' : v));
    }
  }, [location.pathname]);

  // Clear optimistic roadmap completion when leaving the roadmap graph (not the language menu)
  useEffect(() => {
    if (view !== 'roadmap') setLastCompletedNode(null);
  }, [view]);

  /** Leaving study for Profile: re-open Achievements → Skills (or other panel) if the session was started from there. */
  useEffect(() => {
    const from = prevViewRef.current;
    prevViewRef.current = view;
    if (from !== 'study') return;
    if (view === 'profile') {
      const ctx = profileStudyReturnContextRef.current;
      profileStudyReturnContextRef.current = null;
      if (ctx && typeof ctx.achievementsPanelView === 'string' && ctx.achievementsPanelView) {
        setProfileAchievementsPanelRestore({
          achievementsPanelView: ctx.achievementsPanelView,
          ...(typeof ctx.achievementsLanguageFilter === 'string'
            ? { achievementsLanguageFilter: ctx.achievementsLanguageFilter }
            : {}),
          token: Date.now()
        });
      }
    } else {
      profileStudyReturnContextRef.current = null;
    }
  }, [view]);

  // Update navigation history when view changes
  useEffect(() => {
    // Only update history if we're not navigating through history
    if (navigationHistory[historyIndex] !== view) {
      // If we're not at the end of the history, truncate the history
      const newHistory = navigationHistory.slice(0, historyIndex + 1);
      // Add the new view to the history
      newHistory.push(view);
      // Update the history and index
      setNavigationHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [view, navigationHistory, historyIndex]);

  // Listen for custom navigation events
  useEffect(() => {
    const handleNavigateToSubscription = () => {
      if (auth.currentUser?.isAnonymous) {
        setSaveModalOpen(true);
        return;
      }
      setView('subscription');
    };

    window.addEventListener('navigateToSubscription', handleNavigateToSubscription);

    return () => {
      window.removeEventListener('navigateToSubscription', handleNavigateToSubscription);
    };
  }, []);

  const handleStartStudying = (item) => {
    // Save the current view before changing to study mode
    setPreviousView(view);
    if (view === 'profile' && item && typeof item === 'object' && item.studyReturnContext) {
      profileStudyReturnContextRef.current = item.studyReturnContext;
    } else {
      profileStudyReturnContextRef.current = null;
    }
    if (view === 'roadmap' || view === 'roadmap-menu') {
      roadmapStudyReturnLocationRef.current = {
        pathname: location.pathname,
        search: location.search || '',
        state: location.state
      };
    } else {
      roadmapStudyReturnLocationRef.current = null;
    }

    // Clear search term when starting study mode
    setSearchTerm('');

    // Check if this is a special navigation request
    if (item && item.type === 'navigate') {
      // Handle navigation to a specific view
      if (item.target === 'creation') {
        setView('creation');
      } else {
        console.warn('Unknown navigation target:', item.target);
      }
      return;
    }

    // console.log("App.js - handleStartStudying called with item:", item);

    if (item && typeof item === 'object' && item.deckId && item.dueOnly) {
      // SRS dashboard: study only due cards in this cookbook
      setSelectedDeck(item);
      setStudyItem(null);
    } else if (item && typeof item === 'object' && item.deckId) {
      // Deck id + options (e.g. roadmap test mode, preloaded cards from roadmap ensure)
      setSelectedDeck(item);
      setStudyItem(Array.isArray(item.cards) ? item.cards : null);
    } else if (typeof item === 'string') {
      // String deck ID - just the deck
      setSelectedDeck(item);
      setStudyItem(null);
    } else if (item && item.type === 'deck') {
      // Deck object with possible startCardId
      console.log('App.js - Received deck object:', item);
      setSelectedDeck(item);  // Pass the whole object through
      setStudyItem(null);
    } else if (
      item &&
      typeof item === 'object' &&
      item.practiceStartedSession &&
      Array.isArray(item.cards)
    ) {
      // Roadmap practice queue: started snippets that are not due today (may span cookbooks)
      setSelectedDeck(item);
      setStudyItem(item.cards);
    } else if (item && typeof item === 'object' && item.deckId && Array.isArray(item.cards)) {
      // Legacy deck-backed card list — prefer the unified deckId branch above
      setSelectedDeck(item);
      setStudyItem(item.cards);
    } else if (Array.isArray(item)) {
      setSelectedDeck(null);
      setStudyItem(item);
    } else {
      console.warn('Study payload not supported (legacy snippet study removed):', item);
      return;
    }

    if (isAnonymousGuest && (view === 'roadmap' || view === 'roadmap-menu')) {
      const cookbookId =
        typeof item === 'string' ? item : item?.deckId || item?.id || undefined;
      const trackId = item && typeof item === 'object' ? item.trackId : undefined;
      if (markGuestDemoStarted('track-study')) {
        trackDemoStarted({
          surface: 'track-study',
          ...(cookbookId ? { cookbookId } : {}),
          ...(trackId ? { trackId } : {}),
        });
      }
    }
    setView('study');
  };

  const handleDeckCreated = (deckId, deckData) => {
    // Optionally switch to deck manager view to show the newly created deck
    console.log('New deck created:', deckId, deckData);
    // You could add logic here to switch to deck manager view or show a notification
  };

  useEffect(() => {
    if (!isAnonymousGuest) return;
    if (view !== 'roadmap-menu' && view !== 'roadmap') return;
    if (!markGuestDemoStarted('learn-catalog')) return;
    trackDemoStarted({ surface: 'learn-catalog' });
  }, [isAnonymousGuest, view]);

  /**
   * Fired by <Study /> after the first clean Check. Shows the top save
   * banner — guests stay on the study surface without GuestSaveModal or /auth.
   */
  const handleGuestSessionComplete = useCallback((payload) => {
    if (!isAnonymousGuest) return;
    try {
      const total = payload?.totalCount ?? 0;
      const correct = payload?.correctCount ?? 0;
      trackSessionCompleted({ totalCount: total, correctCount: correct });

      const firstNextReviewIso =
        payload?.firstNextReviewIso ||
        (() => {
          const fallback = new Date();
          fallback.setHours(fallback.getHours() + 24);
          return fallback.toISOString();
        })();
      const dueLabel = payload?.dueLabel || formatGuestDueLabel(firstNextReviewIso);
      setGuestDueLabel(dueLabel);
      markGuestStudyCompleted({ firstNextReviewIso });
    } catch (err) {
      console.warn('[guest-session-complete] tracking failed:', err);
    }
    setSaveModalOpen(false);
    setSoftGateVisible(true);
  }, [isAnonymousGuest]);

  const guestShowBrowseTracksCta = isAnonymousGuest && !hasCompletedGuestStudy();

  const handleGuestExploreRoadmap = useCallback(() => {
    setPreviousView(view);
    navigate(ROADMAP_PATHS.menu);
    setView('roadmap-menu');
    setSearchTerm('');
  }, [navigate, view]);

  const handleBackToHome = () => {
    setSelectedDeck(null);
    setView('home');
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const toggleCreationMenu = () => {
    if (isAnonymousGuest) {
      navigateGuestSignup();
      return;
    }
    // Save the current view before changing to creation mode
    setPreviousView(view);
    setView('creation');
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setView('course-viewer');
  };

  const handleBackToCourses = () => {
    setSelectedCourse(null);
    setView('learn');
  };

  const handleShowCourseGenerator = () => {
    setShowCourseGenerator(true);
    setView('course-generator');
  };

  const handleDeleteCourse = async (courseId) => {
    try {
      // Delete the course document from Firestore
      const courseRef = doc(db, `users/${user.uid}/courses/${courseId}`);
      await deleteDoc(courseRef);

      // Clear selected course if it's the one being deleted
      if (selectedCourse?.id === courseId) {
        setSelectedCourse(null);
        setView('learn'); // Go back to course list
      }

      console.log('Course and associated snippets deleted successfully');
    } catch (error) {
      console.error('Error deleting course:', error);
      throw error; // Re-throw so the CourseViewer can handle the error
    }
  };

  const handleBackToCourseList = () => {
    setShowCourseGenerator(false);
    setView('learn');
  };

  const handleGenerateCourse = async (courseData) => {
    try {
      console.log('=== COURSE GENERATION START ===');
      console.log('Generating course with data:', courseData);

      const functions = getFunctions();
      const generateCourseFunction = httpsCallable(functions, 'generateCourseOutline');

      const result = await generateCourseFunction({
        userPrompt: courseData.prompt,
        language: courseData.language,
        difficulty: courseData.difficulty,
        topic: courseData.topic
      });

      console.log('Course generation result:', result.data);
      const { courseId, courseStructure } = result.data;

      // Navigate back to learn mode to show the new course
      setView('learn');
      setShowCourseGenerator(false);

    } catch (error) {
      console.error('Error generating course:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        details: error.details
      });

      // Show user-friendly error message
      alert(`Failed to generate course: ${error.message || 'Unknown error occurred'}`);
    }
  };

  const handleGenerateCookbookOnly = async (courseData) => {
    try {
      console.log('=== COOKBOOK GENERATION START ===');
      console.log('Generating cookbook only with data:', courseData);

      const functions = getFunctions();
      const generateCookbookFunction = httpsCallable(functions, 'generateCookbook');

      // Generate the cookbook directly (no course creation)
      console.log('Calling generateCookbook function...');
      const result = await generateCookbookFunction({
        userPrompt: courseData.prompt,
        language: courseData.language,
        difficulty: courseData.difficulty,
        topic: courseData.topic
      });

      console.log('Cookbook generation result:', result.data);
      const { cookbookId } = result.data;

      if (!cookbookId) {
        throw new Error('Cookbook was not created during generation');
      }

      console.log('Cookbook created successfully with ID:', cookbookId);
      console.log('=== COOKBOOK GENERATION COMPLETE ===');

      // Navigate to study mode with the cookbook
      setShowCourseGenerator(false);
      handleStartStudying(cookbookId);

    } catch (error) {
      console.error('Error generating cookbook:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        details: error.details
      });

      // Extract error message from Firebase error structure
      const errorMessage = error.message ||
        (error.details && typeof error.details === 'string' ? error.details : null) ||
        'Unknown error occurred';

      // Check if it's a token-related error
      if (error.code === 'failed-precondition' || errorMessage.includes('Insufficient tokens')) {
        // Re-throw to let CourseGenerator handle it with token UI
        throw new Error(errorMessage);
      }

      // Show user-friendly error message for other errors
      alert(`Failed to generate cookbook: ${errorMessage}`);
    }
  };

  const handleCourseProgressUpdate = async (courseId, lessonIndex, subLessonIndex, completed) => {
    try {
      const courseRef = doc(db, `users/${user.uid}/courses/${courseId}`);

      await updateDoc(courseRef, {
        [`progress.completedLessons.${lessonIndex}.${subLessonIndex}`]: completed,
        [`progress.currentLesson`]: lessonIndex,
        [`progress.currentSubLesson`]: subLessonIndex,
        updatedAt: new Date()
      });

      // Update the selected course state if it's the current one
      if (selectedCourse?.id === courseId) {
        setSelectedCourse(prev => ({
          ...prev,
          progress: {
            ...prev.progress,
            completedLessons: {
              ...prev.progress.completedLessons,
              [lessonIndex]: {
                ...prev.progress.completedLessons[lessonIndex],
                [subLessonIndex]: completed
              }
            },
            currentLesson: lessonIndex,
            currentSubLesson: subLessonIndex
          }
        }));
      }

    } catch (error) {
      console.error('Error updating course progress:', error);
    }
  };

  const renderLibraryContent = () => {
    switch (activeTab) {
      case 'cookbooks':
        return (
          <DeckManager
            userId={user.uid}
            onStartStudying={handleStartStudying}
            initialActiveTab="cookbooks"
            searchQuery={searchTerm}
            libraryNavNonce={libraryNavNonce}
            onLibraryDeckEditorOpenChange={setLibraryDeckEditorOpen}
          />
        );
      case 'collections':
        return (
          <DeckManager
            userId={user.uid}
            onStartStudying={handleStartStudying}
            initialActiveTab="collections"
            searchQuery={searchTerm}
            libraryNavNonce={libraryNavNonce}
            onLibraryDeckEditorOpenChange={setLibraryDeckEditorOpen}
          />
        );
      default:
        return (
          <DeckManager
            userId={user.uid}
            onStartStudying={handleStartStudying}
            initialActiveTab="cookbooks"
            searchQuery={searchTerm}
            libraryNavNonce={libraryNavNonce}
            onLibraryDeckEditorOpenChange={setLibraryDeckEditorOpen}
          />
        );
    }
  };

  // Error fallback UI
  const renderErrorFallback = () => {
    return (
      <div className="error-container">
        <h2>Something went wrong</h2>
        <p>{errorMessage || 'An unexpected error occurred. Please try refreshing the page.'}</p>
        <button
          onClick={() => {
            setHasError(false);
            setErrorMessage('');
            window.location.reload();
          }}
          className="retry-button"
        >
          Retry
        </button>
      </div>
    );
  };

  const normalizedPath = (location.pathname || '/').replace(/\/$/, '') || '/';

  /** Keep React Router URL aligned with header history (same rules as forward). */
  const navigateToMatchView = (targetView) => {
    if (targetView === 'roadmap-menu') {
      navigate(ROADMAP_PATHS.menu);
    } else if (targetView === 'roadmap') {
      navigate(ROADMAP_PATHS.graph);
    } else if (targetView === 'profile') {
      navigate('/home');
    } else {
      navigate('/home');
    }
  };

  const showPartnerAccessBanner = useMemo(
    () => shouldShowPartnerAccessBanner({
      isAnonymousGuest,
      hasPaidSubscription,
      partnerBannerDismissed,
      partnerProUntil,
    }),
    [isAnonymousGuest, hasPaidSubscription, partnerBannerDismissed, partnerProUntil]
  );

  return (
    <div
      className={[
        'app',
        view === 'roadmap' || view === 'roadmap-menu' ? 'app--roadmap' : '',
        view === 'home' || view === 'home-srs' ? 'app--home' : '',
        view === 'community' || view === 'shared' ? 'app--community' : '',
        view === 'settings' ? 'app--settings' : '',
        view === 'study' ? 'app--study' : '',
        view === 'playground' ? 'app--playground' : '',
        softGateVisible ? 'app--soft-gated' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {softGateVisible && isAnonymousGuest && (
        <GuestSoftGateBanner
          dueLabel={guestDueLabel}
          onSave={openSaveModal}
          onExplore={handleGuestExploreRoadmap}
          onDismiss={dismissSoftGate}
        />
      )}
      {showPartnerAccessBanner && (
        <PartnerAccessBanner
          partnerProUntil={partnerProUntil}
          onUpgrade={navigateToSubscriptionPage}
          onDismiss={() => setPartnerBannerDismissed(true)}
        />
      )}
      {isAnonymousGuest && (
        <GuestSaveModal
          open={saveModalOpen}
          onSaved={handleSaveModalSaved}
          onDismiss={handleSaveModalDismiss}
          dueLabel={guestDueLabel}
        />
      )}
      {!isAnonymousGuest && (
        <RolePickerModal
          open={showRolePicker}
          userId={user?.uid}
          onComplete={handleRolePickerComplete}
        />
      )}
      <header className="app-header">
        <div className="header-left">
          <div className="app-header__brand-wrap">
            <div className="app-header__brand">
              <span className="app-header__brand-title">FlashCode</span>
              <span className="app-header__brand-tagline" aria-hidden="true">
                <span className="app-header__brand-tagline-text">The gym for code</span>
              </span>
            </div>
          </div>
        </div>
        <div className="search-container">
          {/* <button
            className="app-header__token-display-btn"
            onClick={() => {
              try {
                const event = new CustomEvent('openTokenUI');
                window.dispatchEvent(event);
              } catch (e) {
                console.log('Token UI not implemented yet.');
              }
            }}
            title={`Tokens: ${tokenBalance}`}
          >
            Tokens: {tokenBalance}
          </button> */}
          <div className="navigation-buttons">
            <button
              className="header-nav-button header-back-nav-button"
              onClick={() => {
                const path = (location.pathname || '/').replace(/\/$/, '') || '/';

                if (view === 'study') {
                  attemptStudyExit();
                  return;
                }

                if (path === ROADMAP_PATHS.graph) {
                  if (location.state?.roadmapNavFromHome) {
                    if (historyIndex > 0) {
                      const newIndex = historyIndex - 1;
                      const prevView = navigationHistory[newIndex];
                      setHistoryIndex(newIndex);
                      setView(prevView);
                      navigateToMatchView(prevView);
                    } else {
                      navigate('/home', { replace: true });
                      setView('home');
                    }
                    return;
                  }
                  if (location.state?.roadmapNavFromProfile) {
                    if (historyIndex > 0) {
                      const newIndex = historyIndex - 1;
                      const prevView = navigationHistory[newIndex];
                      setHistoryIndex(newIndex);
                      setView(prevView);
                      navigateToMatchView(prevView);
                    } else {
                      navigate('/home', { replace: true });
                      setView('profile');
                    }
                    return;
                  }
                  navigate(ROADMAP_PATHS.menu);
                  if (historyIndex > 0) {
                    setHistoryIndex(historyIndex - 1);
                  }
                  return;
                }
                if (path === ROADMAP_PATHS.menu) {
                  if (historyIndex > 0) {
                    const newIndex = historyIndex - 1;
                    const prevView = navigationHistory[newIndex];
                    setHistoryIndex(newIndex);
                    setView(prevView);
                    navigateToMatchView(prevView);
                  } else {
                    navigate('/home');
                    setView('home');
                  }
                  return;
                }
                if (view === 'library' && libraryDeckEditorOpen) {
                  setLibraryNavNonce((n) => n + 1);
                  return;
                }
                if (historyIndex > 0) {
                  const newIndex = historyIndex - 1;
                  const prevView = navigationHistory[newIndex];
                  setHistoryIndex(newIndex);
                  setView(prevView);
                  navigateToMatchView(prevView);
                }
              }}
              disabled={
                view === 'study'
                  ? historyIndex <= 0 &&
                  previousView !== 'roadmap' &&
                  previousView !== 'roadmap-menu'
                  : normalizedPath === ROADMAP_PATHS.menu || normalizedPath === ROADMAP_PATHS.graph
                    ? false
                    : historyIndex <= 0 && !(view === 'library' && libraryDeckEditorOpen)
              }
              title="Navigate Back"
            >
              <NavBackIcon />
            </button>
            <button
              className="header-nav-button header-forward-nav-button"
              onClick={() => {
                if (historyIndex < navigationHistory.length - 1) {
                  const newIndex = historyIndex + 1;
                  const nextView = navigationHistory[newIndex];
                  setHistoryIndex(newIndex);
                  setView(nextView);
                  if (nextView === 'roadmap-menu') {
                    navigate(ROADMAP_PATHS.menu);
                  } else if (nextView === 'roadmap') {
                    navigate(ROADMAP_PATHS.graph);
                  } else if (nextView === 'profile') {
                    navigate('/home');
                  } else {
                    navigate('/home');
                  }
                }
              }}
              disabled={historyIndex >= navigationHistory.length - 1}
              title="Navigate Forward"
            >
              <NavForwardIcon />
            </button>
          </div>
          <span className="app-header__search-divider" aria-hidden />
          <div className="app-header__search-field">
            <SearchIcon className="app-header__search-field-icon" />
            <input
              type="text"
              placeholder="Search Cookbooks, Snippets..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-bar"
            />
          </div>
        </div>
        <div className="user-controls">

          <button className="home-create-button" onClick={toggleCreationMenu}>
            <img src={addIcon} alt="Add" className="create-icon" />
            <span style={{ fontWeight: 'bold' }}>Create</span>
          </button>
          <DailyGoalHeader
            userId={user.uid}
            hideForGuest={isAnonymousGuest}
            dueItemsCount={dueItemsCount}
            onReviewClick={() => setView('home-srs')}
          />
          <div className="app-header-user-account-wrap">
            <button
              className="user-icon-btn"
              onClick={() => {
                setView('profile');
                setSearchTerm('');
                setShowDropdown(false);
              }}
              type="button"
              aria-label="Profile"
            >
              {user.photoURL ? (
                <img
                  className="avatar-image"
                  src={user.photoURL}
                  alt=""
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="profile-icon">
                  {(user.displayName && user.displayName[0]) ||
                    (user.isAnonymous ? 'G' : 'U')}
                </div>
              )}
            </button>
            <button
              className="app-header-account-dropdown-trigger"
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdown();
              }}
              type="button"
              aria-label="Account menu"
              aria-expanded={showDropdown}
              aria-haspopup="menu"
            >
              <ChevronDown className="app-header-account-dropdown-trigger-icon" size={14} strokeWidth={2} aria-hidden />
            </button>
            {showDropdown && (
              <div className="dropdown-menu dropdown-menu--account" role="menu">
                {user.isAnonymous ? (
                  <>
                    <div className="app-header-account-dropdown-header">
                      <span className="app-header-account-dropdown-email">
                        Guest. Sign up to sync progress across devices.
                      </span>
                    </div>
                    <div className="app-header-account-dropdown-section">
                      <button
                        type="button"
                        className="app-header-account-dropdown-item"
                        role="menuitem"
                        onClick={() => {
                          navigateGuestSignup();
                          setShowDropdown(false);
                        }}
                      >
                        <span className="app-header-account-dropdown-item-icon-slot" aria-hidden="true">
                          <UserPlus className="app-header-account-dropdown-item-icon" size={16} strokeWidth={2} />
                        </span>
                        <span className="app-header-account-dropdown-item-label">Sign up</span>
                      </button>
                      <button
                        type="button"
                        className="app-header-account-dropdown-item"
                        role="menuitem"
                        onClick={() => {
                          navigate('/auth');
                          setShowDropdown(false);
                        }}
                      >
                        <span className="app-header-account-dropdown-item-icon-slot" aria-hidden="true">
                          <LogIn className="app-header-account-dropdown-item-icon" size={16} strokeWidth={2} />
                        </span>
                        <span className="app-header-account-dropdown-item-label">Log in</span>
                      </button>
                    </div>
                    <div className="app-header-account-dropdown-divider" role="separator" aria-hidden="true" />
                    <div className="app-header-account-dropdown-section">
                      <a
                        className="app-header-account-dropdown-item"
                        href="mailto:privacy@flashcode.tech"
                        role="menuitem"
                        onClick={() => setShowDropdown(false)}
                      >
                        <span className="app-header-account-dropdown-item-icon-slot" aria-hidden="true">
                          <Mail className="app-header-account-dropdown-item-icon" size={16} strokeWidth={2} />
                        </span>
                        <span className="app-header-account-dropdown-item-label">
                          Support
                          <span className="app-header-account-dropdown-support-separator" aria-hidden="true"> · </span>
                          Feedback
                        </span>
                      </a>
                      <a
                        className="app-header-account-dropdown-item"
                        href="https://discord.gg/teq5p9P29K"
                        role="menuitem"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setShowDropdown(false)}
                      >
                        <span className="app-header-account-dropdown-item-icon-slot" aria-hidden="true">
                          <FaDiscord className="app-header-account-dropdown-item-icon app-header-account-dropdown-item-icon--discord" />
                        </span>
                        <span className="app-header-account-dropdown-item-label">Join Discord</span>
                      </a>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="app-header-account-dropdown-header">
                      <span className="app-header-account-dropdown-email">{user.email}</span>
                    </div>
                    <div className="app-header-account-dropdown-section">
                      <button
                        type="button"
                        className="app-header-account-dropdown-item"
                        role="menuitem"
                        onClick={() => {
                          setView('subscription');
                          setShowDropdown(false);
                        }}
                      >
                        <span className="app-header-account-dropdown-item-icon-slot" aria-hidden="true">
                          <ArrowUpCircle className="app-header-account-dropdown-item-icon" size={16} strokeWidth={2} />
                        </span>
                        <span className="app-header-account-dropdown-item-label">Upgrade to Premium</span>
                      </button>
                      <button
                        type="button"
                        className="app-header-account-dropdown-item"
                        role="menuitem"
                        onClick={async () => {
                          setShowDropdown(false);
                          try {
                            await auth.signOut();
                          } finally {
                            navigate('/auth');
                          }
                        }}
                      >
                        <span className="app-header-account-dropdown-item-icon-slot" aria-hidden="true">
                          <LogOut className="app-header-account-dropdown-item-icon" size={16} strokeWidth={2} />
                        </span>
                        <span className="app-header-account-dropdown-item-label">Sign Out</span>
                      </button>
                    </div>
                    <div className="app-header-account-dropdown-divider" role="separator" aria-hidden="true" />
                    <div className="app-header-account-dropdown-section">
                      <a
                        className="app-header-account-dropdown-item"
                        href="mailto:privacy@flashcode.tech"
                        role="menuitem"
                        onClick={() => setShowDropdown(false)}
                      >
                        <span className="app-header-account-dropdown-item-icon-slot" aria-hidden="true">
                          <Mail className="app-header-account-dropdown-item-icon" size={16} strokeWidth={2} />
                        </span>
                        <span className="app-header-account-dropdown-item-label">
                          Support
                          <span className="app-header-account-dropdown-support-separator" aria-hidden="true"> · </span>
                          Feedback
                        </span>
                      </a>
                      <a
                        className="app-header-account-dropdown-item"
                        href="https://discord.gg/teq5p9P29K"
                        role="menuitem"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setShowDropdown(false)}
                      >
                        <span className="app-header-account-dropdown-item-icon-slot" aria-hidden="true">
                          <FaDiscord className="app-header-account-dropdown-item-icon app-header-account-dropdown-item-icon--discord" />
                        </span>
                        <span className="app-header-account-dropdown-item-label">Join Discord</span>
                      </a>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {isPanelVisible && (
        <nav className={`side-panel ${isPanelVisible ? 'visible' : 'hidden'}`}>
          <button
            className={`side-nav-button ${view === 'home' ? 'active' : ''}`}
            onClick={() => {
              if (view === 'study') {
                attemptStudyExit(() => {
                  navigate('/home');
                  setSelectedDeck(null);
                  setStudyItem(null);
                  setView('home');
                  setActiveTab('cookbooks');
                  setSearchTerm('');
                  refreshDueItemsCount();
                });
                return;
              }
              navigate('/home');
              setView('home');
              setActiveTab('cookbooks');
              setSearchTerm(''); // Clear search when navigating
            }}
            title="Home"
          >
            <div className="nav-button-content">
              <DeckIcon />
              <span className="side-panel__nav-label">Home</span>
            </div>
          </button>
          <button
            className={`side-nav-button ${view === 'library' ? 'active' : ''}`}
            onClick={() => {
              if (view === 'study') {
                attemptStudyExit(() => {
                  navigate('/home');
                  setSelectedDeck(null);
                  setStudyItem(null);
                  setView('library');
                  setActiveTab('cookbooks');
                  setSearchTerm('');
                  setLibraryNavNonce((n) => n + 1);
                  refreshDueItemsCount();
                });
                return;
              }
              navigate('/home');
              setView('library');
              setActiveTab('cookbooks');
              setSearchTerm(''); // Clear search when navigating
              setLibraryNavNonce((n) => n + 1);
            }}
            title="Library"
          >
            <div className="nav-button-content">
              <LibraryIcon />
              <span className="side-panel__nav-label">Library</span>
            </div>
          </button>
          <button
            className={`side-nav-button ${view === 'roadmap' || view === 'roadmap-menu' ? 'active' : ''}`}
            onClick={() => {
              if (view === 'study') {
                attemptStudyExit(() => {
                  setPreviousView(view);
                  setSelectedDeck(null);
                  setStudyItem(null);
                  navigate(ROADMAP_PATHS.menu);
                  setView('roadmap-menu');
                  setShowDropdown(false);
                  setSearchTerm('');
                  refreshDueItemsCount();
                });
                return;
              }
              setPreviousView(view);
              navigate(ROADMAP_PATHS.menu);
              setView('roadmap-menu');
              setShowDropdown(false);
              setSearchTerm('');
            }}
            title="Learn"
          >
            <div className="nav-button-content">
              <RoadmapNodeIcon className="app-side-nav-roadmap-icon" />
              <span className="side-panel__nav-label">Learn</span>
            </div>
          </button>

          <button
            className={`side-nav-button ${view === 'playground' ? 'active' : ''}`}
            onClick={() => {
              if (view === 'study') {
                attemptStudyExit(() => {
                  navigate('/home');
                  setSelectedDeck(null);
                  setStudyItem(null);
                  setView('playground');
                  setShowDropdown(false);
                  setSearchTerm('');
                  refreshDueItemsCount();
                });
                return;
              }
              navigate('/home');
              setView('playground');
              setShowDropdown(false);
              setSearchTerm('');
            }}
            title="Playground"
          >
            <div className="nav-button-content">
              <PlaygroundIcon className="app-side-nav-playground-icon" />
              <span className="side-panel__nav-label">Playground</span>
            </div>
          </button>

          {/* Snippet Limit Indicator for free tier users */}
          {/* <SnippetLimitIndicator userId={user.uid} setView={setView} /> */}

          <button
            className={`side-nav-button side-nav-button--community ${view === 'community' || view === 'shared' ? 'active' : ''}`}
            onClick={() => {
              navigate('/home');
              setView('community');
              setSearchTerm('');
            }}
            title="Browse"
          >
            <div className="nav-button-content">
              <CommunityIcon className="app-side-nav-community-icon" />
              <span className="side-panel__nav-label">Browse</span>
            </div>
          </button>

          {/* Settings Button */}
          <button
            className={`side-nav-button side-settings-nav-button ${view === 'settings' ? 'active' : ''}`}
            onClick={() => {
              navigate('/home');
              setView('settings');
              setShowDropdown(false);
              setSearchTerm(''); // Clear search when navigating
            }}
            title="Settings"
          >
            <div className="nav-button-content">
              <SettingsIcon />
              <span className="side-panel__nav-label">Settings</span>
            </div>
          </button>
        </nav>
      )}

      <main className="app-main">
        {searchTerm && searchTerm.trim().length >= 2 ? (
          <ErrorBoundary componentName="GlobalSearch">
            <GlobalSearch
              userId={user.uid}
              searchTerm={searchTerm}
              onStartStudying={handleStartStudying}
            />
          </ErrorBoundary>
        ) : (
          <>
            {view === 'home' || view === 'home-srs' ? (
              <ErrorBoundary componentName="Home">
                <Home
                  userId={user.uid}
                  onStartStudying={handleStartStudying}
                  setView={setView}
                  view={view}
                  libraryNavNonce={libraryNavNonce}
                  dueItemsCount={dueItemsCount}
                  onDueCountRefresh={refreshDueItemsCount}
                  isAnonymousGuest={isAnonymousGuest}
                  guestShowBrowseTracksCta={guestShowBrowseTracksCta}
                  onGuestBrowseTracks={handleGuestExploreRoadmap}
                  onOpenCreation={toggleCreationMenu}
                />
              </ErrorBoundary>
            ) : null}
            {view === 'library' && (
              <ErrorBoundary componentName="Library">
                {renderLibraryContent()}
              </ErrorBoundary>
            )}
            {view === 'study' && (
              <ErrorBoundary componentName="Study">
                <Study
                  key={`${selectedDeck?.practiceStartedSession
                    ? `practice-${selectedDeck?.deckId || selectedDeck?.trackId || 'track'}`
                    : studyItem?.id || selectedDeck?.id || selectedDeck?.deckId || 'study'
                    }-${selectedDeck?.nodeId || selectedDeck?.roadmapNodeId || 'node'}`}
                  userId={user.uid}
                  deck={selectedDeck}  // Pass the full deck object
                  cards={Array.isArray(studyItem) ? studyItem : studyItem ? [studyItem] : undefined}
                  setView={setStudySurfaceView}
                  previousView={previousView}
                  onExitStudy={handleExitStudy}
                  onReviewQueueChanged={refreshDueItemsCountDebounced}
                  onStartStudying={handleStartStudying}
                  onNodeCompleted={(data) => {
                    setLastCompletedNode(data);
                  }}
                  onSessionComplete={isAnonymousGuest ? handleGuestSessionComplete : undefined}
                  showSessionSummary={!isAnonymousGuest}
                  onNavigateRolePath={handleNavigateRolePathFromStudy}
                  onRegisterStudyExitHandler={
                    isAnonymousGuest ? undefined : handleRegisterStudyExitHandler
                  }
                  title={Array.isArray(studyItem) && studyItem.length > 1 ? "Divider Cookbook" : undefined}
                  disableAiFeatures={isAnonymousGuest}
                  onGuestUpgrade={isAnonymousGuest ? () => setSoftGateVisible(true) : undefined}
                />
              </ErrorBoundary>
            )}
            {view === 'playground' && (
              <ErrorBoundary componentName="Playground">
                <PlaygroundStudyMode userId={user.uid} />
              </ErrorBoundary>
            )}
            {(view === 'community' || view === 'shared') && (
              <ErrorBoundary componentName="Community">
                <CommunityPage
                  userId={user.uid}
                  isAnonymousGuest={isAnonymousGuest}
                  onNavigateToLibrary={() => setView('library')}
                />
              </ErrorBoundary>
            )}
            {view === 'profile' && (
              <ErrorBoundary componentName="UserProfile">
                <UserProfile
                  userId={user.uid}
                  onStartStudying={handleStartStudying}
                  achievementsPanelRestore={profileAchievementsPanelRestore}
                  onAchievementsPanelRestoreConsumed={clearProfileAchievementsPanelRestore}
                  onOpenCommunity={() => setView('community')}
                />
              </ErrorBoundary>
            )}
            {view === 'learn' && (
              <ErrorBoundary componentName="LearnMode">
                <LearnMode
                  userId={user.uid}
                  onModeChange={setView}
                  onCourseSelect={handleCourseSelect}
                  onShowCourseGenerator={handleShowCourseGenerator}
                  onStartStudying={handleStartStudying}
                />
              </ErrorBoundary>
            )}
            {view === 'course-viewer' && selectedCourse && (
              <ErrorBoundary componentName="CourseViewer">
                <CourseViewer
                  course={selectedCourse}
                  userId={user.uid}
                  onBack={handleBackToCourses}
                  onProgressUpdate={handleCourseProgressUpdate}
                  onModeChange={setView}
                  onDeleteCourse={handleDeleteCourse}
                  onStartStudying={handleStartStudying}
                  onDeckCreated={handleDeckCreated}
                />
              </ErrorBoundary>
            )}
            {view === 'course-generator' && (
              <ErrorBoundary componentName="CourseGenerator">
                <div className="course-generator-section">
                  {/* <div className="section-header">
                    <button
                      className="back-btn"
                      onClick={handleBackToCourseList}
                    >
                      ←
                    </button>
                  </div> */}
                  <CourseGenerator
                    onGenerate={handleGenerateCourse}
                    onGenerateCookbookOnly={handleGenerateCookbookOnly}
                    tokenBalance={tokenBalance}
                    onTokenBalanceUpdate={setTokenBalance}
                  />
                </div>
              </ErrorBoundary>
            )}
            {(view === 'roadmap' || view === 'roadmap-menu') && (
              <ErrorBoundary componentName="RoadmapView">
                <div className="app-main__roadmap-wrap">
                  <RoadmapView
                    userId={user?.uid}
                    onStartStudying={handleStartStudying}
                    onGoToReview={() => setView('home-srs')}
                    lastCompletedNode={lastCompletedNode}
                  />
                </div>
              </ErrorBoundary>
            )}
            {view === 'settings' && (
              <ErrorBoundary componentName="Settings">
                <Settings userId={user.uid} />
              </ErrorBoundary>
            )}
            {view === 'subscription' && (
              <ErrorBoundary componentName="SubscriptionPage">
                <SubscriptionPage />
              </ErrorBoundary>
            )}
            {view === 'creation' && (
              <ErrorBoundary componentName="UserCreation">
                <UserCreation
                  userId={user.uid}
                  previousView={previousView}
                  onClose={() => setView(previousView)}
                />
              </ErrorBoundary>
            )}
          </>
        )}
      </main>

      <TokenPurchaseModal
        isOpen={showTokenModal}
        onClose={() => setShowTokenModal(false)}
        tokenBalance={tokenBalance}
      />
    </div>
  );
}

export default App;
